'use client';

import Box from '@mui/material/Box';
import {
    GRUND,
    Gradientfält,
    TYPSNITT,
    TextAvtäckning,
    hexTillRgba,
    ljus
} from 'src/components/rörelse';
import { RouterLink } from 'src/routes/components';
import { paths } from 'src/routes/paths';
import { Motivminiatyr } from './komponenter/Motivminiatyr';
import { PRESENTATIONER, type Presentationsslug } from './presentationer';

const RUTT: Record<Presentationsslug, string> = {
    autopilot: paths.presentation.autopilot,
    handelser: paths.presentation.händelser,
    bank: paths.presentation.bank,
    styrelsen: paths.presentation.styrelsen,
    'mitt-hem': paths.presentation.mittHem
};

export function PresentationIndexView() {
    return (
        <>
            <Gradientfält
                kombinationer={['blå', 'lila', 'grön']}
                positioner={[
                    [18, 22],
                    [82, 40],
                    [46, 82]
                ]}
                index={1}
                styrka={0.34}
            />

            <Box
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    px: { xs: 3, sm: 6, md: 10 },
                    pt: { xs: 12, md: 18 },
                    pb: { xs: 8, md: 12 },
                    maxWidth: 1320,
                    mx: 'auto'
                }}
            >
                <TextAvtäckning
                    text="Fem sätt att driva en förening"
                    component="h1"
                    variant="h1"
                    avstånd={24}
                    sx={{
                        fontFamily: TYPSNITT.display,
                        fontVariationSettings: "'opsz' 120",
                        fontWeight: 600,
                        fontSize: 'clamp(2.6rem, 6.4vw, 5.6rem)',
                        lineHeight: 1.02,
                        letterSpacing: '-0.032em',
                        textWrap: 'balance',
                        maxWidth: '18ch',
                        color: GRUND.text
                    }}
                />
                <TextAvtäckning
                    text="Var och en tar ett flöde i Flinky och följer det hela vägen igenom. Läs dem i vilken ordning som helst."
                    fördröjning={160}
                    sx={{
                        mt: 3,
                        maxWidth: '58ch',
                        fontSize: { xs: '1.02rem', md: '1.14rem' },
                        lineHeight: 1.6,
                        color: GRUND.dämpad
                    }}
                />
            </Box>

            <Box
                component="ul"
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    listStyle: 'none',
                    m: 0,
                    px: { xs: 3, sm: 6, md: 10 },
                    pb: { xs: 12, md: 18 },
                    maxWidth: 1320,
                    mx: 'auto',
                    display: 'grid',
                    gap: { xs: 2.5, md: 3 },
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(6, 1fr)' }
                }}
            >
                {PRESENTATIONER.map((p, i) => (
                    <Box
                        key={p.slug}
                        component="li"
                        sx={{
                            // Medvetet ojämn rytm: de två första korten är breda, de tre
                            // sista smala. Ett rutnät av likadana kort hade sagt att sidorna
                            // är utbytbara, och det är precis vad de inte är.
                            gridColumn: { lg: i < 2 ? 'span 3' : 'span 2' }
                        }}
                    >
                        <Box
                            component={RouterLink}
                            href={RUTT[p.slug]}
                            sx={{
                                display: 'block',
                                height: '100%',
                                p: { xs: 2.5, md: 3 },
                                borderRadius: 2,
                                textDecoration: 'none',
                                color: 'inherit',
                                bgcolor: hexTillRgba('#12151F', 0.78),
                                border: `1px solid ${GRUND.kant}`,
                                transition: 'border-color .2s, transform .2s',
                                '&:hover': {
                                    borderColor: hexTillRgba(ljus(p.kombo), 0.55),
                                    transform: 'translateY(-2px)'
                                }
                            }}
                        >
                            <Motivminiatyr motiv={p.motiv} kombo={p.kombo} />
                            <Box
                                component="h2"
                                sx={{
                                    fontFamily: TYPSNITT.display,
                                    fontVariationSettings: "'opsz' 40",
                                    fontWeight: 600,
                                    fontSize: i < 2 ? 'clamp(1.5rem, 2.6vw, 2rem)' : '1.25rem',
                                    lineHeight: 1.14,
                                    letterSpacing: '-0.022em',
                                    textWrap: 'balance',
                                    m: 0,
                                    mt: 2.5,
                                    color: GRUND.text
                                }}
                            >
                                {p.titel}
                            </Box>
                            <Box sx={{ mt: 1.25, fontSize: 14.5, lineHeight: 1.55, color: GRUND.dämpad }}>
                                {p.beskrivning}
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Box>
        </>
    );
}
